<script setup lang="ts">
import { useRefereshProductStore } from '@/products/commands/refreshProductStore';
import { useRefreshStorageAreaStore } from '@/storageAreas/stores/refreshStorageAreaStore';
import { storeToRefs } from 'pinia';
import { DataTable, Column, InputText, InputIcon, IconField } from 'primevue';
import { useProductGroupStore } from 'shared/products/groups/stores/productGroupStore';
import { useProductStore } from 'shared/products/stores/productStore';
import { useStorageAreaStore } from 'shared/storageAreas/stores/storageAreaStore';
import { useTranslations } from 'shared/translations/composables/useTranslations';
import { computed, onMounted, onUnmounted, ref } from 'vue';
import { useRefereshProductGroupStore } from '@/products/groups/commands/refreshProductGroupStore';

const storageAreaStore = useStorageAreaStore();
const storageAreas = storeToRefs(storageAreaStore).storageAreas;
const productStore = useProductStore();
const refreshProductStore = useRefereshProductStore();
const productGroupStore = useProductGroupStore();
const refreshProductGroupStore = useRefereshProductGroupStore();
const translations = useTranslations();
const refreshStorageAreaStore = useRefreshStorageAreaStore();
const dashboardInfo = ref<DashboardInfo>({ productGroups: new Map() });

const intervalId = ref<number | null>(null);

onMounted(async () => {
  await refreshStorageAreaStore.initialize();
  await refreshProductStore.initialize();
  await refreshProductGroupStore.initialize();

  updateData();

  intervalId.value = window.setInterval(async () => {
    updateData();
  }, 10000);
});
const filters = ref({
  global: {
    value: null,
    matchMode: 'contains',
  },
});
onUnmounted(() => {
  if (intervalId.value) {
    clearInterval(intervalId.value);
  }
});

function updateData() {
  const info: DashboardInfo = {
    productGroups: new Map(),
  };

  // Loopar igenom alla lagringsområden (storageAreas)
  for (const storageArea of storageAreas.value) {
    // Hoppa över lagringsområden som inte har logYardData
    if (!storageArea.logYardData) continue;

    // Loopar igenom varje "content" i logYardData, varje content representerar en produkt med antal
    for (const content of storageArea.logYardData.content) {
      // Hitta produkten i productStore baserat på productId
      const product = productStore.getById(content.productId);
      if (!product) continue;

      // Hitta produktgruppen för produkten
      const productGroup = productGroupStore.getById(product.productGroupId);

      //få fram FIFO-värden
      let daysSinceFirstMove: null | number = null;
      if (storageArea.logYardData?.firstMoveToUtc) {
        const date = new Date(storageArea.logYardData.firstMoveToUtc);
        daysSinceFirstMove = Math.round((Date.now() - date.getTime()) / 86400000);
      }
      // Standardvärden om produkt eller grupp inte hittas
      let productGroupName = translations.value.generic.unknown;
      let productName = translations.value.generic.unknown;

      if (productGroup) {
        // Sätt namn på produktgrupp och produkt
        productGroupName = productGroup.name;
        productName = product.name;

        // Kolla om denna produktgrupp redan finns i dashboardInfo
        let existingGroup = info.productGroups.get(productGroup.id);

        if (!existingGroup) {
          existingGroup = {
            name: productGroup.name,
            products: new Map(),
            id: productGroup.id,
          };
          info.productGroups.set(productGroup.id, existingGroup);
        }

        let existingProduct = existingGroup.products.get(product.id);

        if (!existingProduct) {
          existingProduct = {
            name: product.name,
            numberOfLogs: 0,
            daysSinceFirstMove: daysSinceFirstMove,
            id: product.id,
          };
          existingGroup.products.set(product.id, existingProduct);
        }

        existingProduct.numberOfLogs += content.numberOfLogs;
        if (existingProduct.daysSinceFirstMove === null) {
          existingProduct.daysSinceFirstMove = daysSinceFirstMove;
        } else if (daysSinceFirstMove !== null) {
          existingProduct.daysSinceFirstMove = Math.min(
            daysSinceFirstMove,
            existingProduct.daysSinceFirstMove,
          );
        }
      }
    }
  }

  dashboardInfo.value = info;
}

interface DashboardInfo {
  productGroups: Map<string, ProductGroup>;
}

interface ProductGroup {
  id: string;
  name: string;
  products: Map<string, Product>;
}

interface Product {
  id: string;
  name: string;
  numberOfLogs: number;
  daysSinceFirstMove: number | null;
}
</script>
<template>
  <div class="header">
    <div class="p-2">
      <span class="text-xl font-bold">Lagersaldo</span>
    </div>
    <div class="flex justify-end">
      <IconField>
        <InputIcon>
          <i class="pi pi-search"></i>
        </InputIcon>
        <InputText
          class="search-field"
          autofocus
          v-model="filters.global.value"
          :placeholder="translations.generic.search"
        />
      </IconField>
    </div>
  </div>
  <div class="dashboard-tables">
    <template
      v-for="group in Array.from(dashboardInfo.productGroups.values()).sort((a, b) =>
        a.name.localeCompare(b.name, undefined, { numeric: true }),
      )"
      :key="group.id"
    >
      <div class="group-table">
        <!-- Rubrik = produktgrupp -->
        <h3 class="group-title">{{ group.name }}</h3>

        <!-- PrimeVue DataTable -->
        <DataTable
          :value="
            Array.from(group.products.values()).sort((a, b) =>
              a.name.localeCompare(b.name, undefined, { numeric: true }),
            )
          "
          v-model:filters="filters"
          :striped-rows="true"
        >
          <Column field="name" :header="translations.generic.product" />
          <Column field="numberOfLogs" :header="translations.generic.balance" />
          <Column field="daysSinceFirstMove" :header="translations.generic.fifo" />
        </DataTable>
      </div>
    </template>
  </div>
</template>
<style scoped>
.header {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
}
.dashboard-tables {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(600px, 1fr));
  gap: 1rem;
  height: 100%;
  overflow-y: auto;
}

.group-table {
  margin: 2rem;
  border-radius: 4px;
}

.group-title {
  padding: 0.2rem;
  border: 0.5px solid #e0e0e0;
  font-size: 1.5rem;
  text-align: center;
}
</style>
